// MIDAS V3 — Message actions row (Phase 6 + R5 Phase B sub-tasks 5a + 5b)
//
// Renders under each assistant response card. 4-icon row left + "helps tune
// Midas" mono label right.
//
//   • Copy      — navigator.clipboard.writeText(message.text). Swap icon to
//                 green Check for 1.5s on success.
//   • Like      — toggles `liked` in midasMessageFeedbackV2Slice and opens
//                 the FeedbackPanelV3 for the user to (optionally) comment.
//                 The POST to /v1/bot/midas/feedback happens ONCE, on panel
//                 submit — carrying action + optional comment.
//   • Dislike   — same as Like, with action="dislike" and an additional
//                 "Was the source wrong?" Yes/No/Skip pill row in the panel.
//   • Regenerate — re-dispatches the chatBotSlice `sendMessage` thunk with
//                 the immediately-prior user message text. NO POST to
//                 /midas/feedback — bot's separate /midas/regenerate endpoint
//                 is out of scope for R5.
//
// R5 Phase B wiring rationale (updated 2026-05-23):
//   • The bot feedback API is keyed by the bot-minted message_id (not our
//     local ChatMessage.id). Phase A landed `botMessageId` extraction from
//     SSE answer/done events. If absent (older bot version), the local
//     toggle still fires but no POST is sent — logged once.
//   • SINGLE POST on panel submit. Original 5a sent an immediate click-time
//     POST and the panel sent a second; the bot's UNIQUE(message_id, action)
//     constraint returned 409 on every second submit. Consolidated: only
//     the panel submit hits the network. If the user closes the panel
//     without typing, no POST happens — accepted trade-off, since an empty
//     "I clicked like" carries little signal.
//   • Regenerate stays local — bot has a separate /midas/regenerate endpoint
//     filed for a future sprint.
//
// Pre-flight findings (documented in main session report):
//   • `chatBotSlice` has NO `regenerateMessage` thunk. The cleanest non-
//     invasive path is to look up the previous user message in
//     `state.chatBot.messages`, then dispatch `sendMessage` with its text +
//     same machine context + same thread origin. The bot streams a fresh
//     response and the existing buffer machinery handles it the same way
//     a normal send does.
//   • The local `midasMessageFeedbackV2Slice` already enforces mutual
//     exclusion in its `toggleLike` / `toggleDislike` reducers.
//   • Tooltip primitives come from v2/ui/tooltip — the project already wraps
//     v3 in a TooltipProvider (see P3 follow-up: artifact pane fixed this).
//
// Spec: MIDAS_V3_PHASE6_STREAMING_ACTIONS_SPEC.md §4 +
//   MIDAS_V3_BUGS_0519_R5_SPEC.md sub-task 5a + 5b.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useMemo, useState } from "react";
import { Copy, ThumbsUp, ThumbsDown, RotateCw, Check } from "lucide-react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import {
  sendMessage,
  clearError,
} from "../../../../shared/chatBot/chatBotSlice";
import {
  toggleLike,
  toggleDislike,
} from "../v2/midasMessageFeedbackV2Slice";
import FeedbackPanelV3 from "./feedback/FeedbackPanelV3";
import useTranslation from "../../../../shared/hooks/useTranslation";

export interface MessageActionsV3Props {
  /** ID of the assistant message this action row belongs to (local ChatMessage.id). */
  messageId: string;
  /** Raw bot text — used as the clipboard payload. We preserve markdown
   *  verbatim (asterisks, lists, pipe tables) so paste into a notepad is the
   *  same as the bot emitted. */
  text: string;
  /** R5 sub-task 5a: bot-minted message_id from the SSE answer/done events.
   *  Required for feedback POSTs. When absent (older bot version), Like/Dislike
   *  toggle locally but no POST is sent. */
  botMessageId?: string;
  /** R5 sub-task 5a: active thread id from midasPageSlice. Forwarded in the
   *  feedback body. Optional — bot accepts null. */
  threadId?: string | null;
}

const MessageActionsV3: React.FC<MessageActionsV3Props> = ({
  messageId,
  text,
  botMessageId,
  threadId,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");

  // ─── Like / Dislike state ───────────────────────────────────────────
  const feedback = useAppSelector(
    (s) => s.midasMessageFeedbackV2.byMessageId[messageId]
  );
  const liked = feedback?.liked || false;
  const disliked = feedback?.disliked || false;

  // ─── Copy state ─────────────────────────────────────────────────────
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard.writeText can reject in non-secure contexts; ignore.
    }
  }, [text]);

  // ─── R5 sub-task 5b: panel visibility ───────────────────────────────
  // `panelAction` is the action currently open in the FeedbackPanelV3. null
  // means no panel is open. We do NOT auto-open the panel for every click —
  // only when the user TURNS ON Like/Dislike (toggleLike flips false → true).
  // Turning OFF (true → false) closes the panel without submitting anything.
  const [panelAction, setPanelAction] = useState<"like" | "dislike" | null>(null);

  // ─── Panel onMessageNotFound callback (passed to FeedbackPanelV3) ─────
  // The panel owns the single POST. If the bot replies 404 (message_not_found),
  // the panel asks us to revert the local v2 toggle so the icon doesn't stay
  // highlighted for a row that never persisted.
  const handlePanelMessageNotFound = useCallback(
    (action: "like" | "dislike") => {
      if (action === "like") {
        dispatch(toggleLike({ messageId }));
      } else {
        dispatch(toggleDislike({ messageId }));
      }
      setPanelAction(null);
    },
    [dispatch, messageId]
  );

  const handleLike = useCallback(() => {
    const willTurnOn = !liked;
    dispatch(toggleLike({ messageId }));
    if (willTurnOn) {
      // Open the panel for optional comment. POST happens once on submit.
      // If botMessageId is missing (older bot version) the panel won't render
      // because showPanel below also gates on botMessageId.
      if (!botMessageId) {
        // eslint-disable-next-line no-console
        console.warn(
          "[midas-v3-feedback] no botMessageId on message — no POST will be sent",
          { localMessageId: messageId }
        );
      }
      setPanelAction("like");
    } else {
      // Turning Like OFF — close panel if open. No DELETE call (bot has no
      // DELETE endpoint per spec). If a POST already landed, the row stays.
      setPanelAction(null);
    }
  }, [liked, dispatch, messageId, botMessageId]);

  const handleDislike = useCallback(() => {
    const willTurnOn = !disliked;
    dispatch(toggleDislike({ messageId }));
    if (willTurnOn) {
      if (!botMessageId) {
        // eslint-disable-next-line no-console
        console.warn(
          "[midas-v3-feedback] no botMessageId on message — no POST will be sent",
          { localMessageId: messageId }
        );
      }
      setPanelAction("dislike");
    } else {
      setPanelAction(null);
    }
  }, [disliked, dispatch, messageId, botMessageId]);

  // ─── Regenerate — re-dispatch sendMessage with the prior user text ──
  //
  // Why this approach: the spec says "use `regenerateMessage` thunk if it
  // exists; otherwise mirror v2's inline approach". V2 has no thunk and no
  // real regeneration — it sets a local `retryRequested` flag and stops.
  // That's not useful UX. The next-best non-invasive approach (per the
  // spec's "match v2 thunk behaviour" clause) is to find the user message
  // immediately preceding this assistant message and re-send it. The bot
  // streams a fresh response; the chat buffer machinery handles it like
  // any other send.
  //
  // We look up the user message AT DISPATCH TIME so we always re-send the
  // most current Redux state, not a snapshot from render time.
  const messages = useAppSelector((s) => s.chatBot.messages);
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const activeThreadIsStreaming = useAppSelector((s) => {
    const tid = s.midasPage.activeThreadId;
    if (!tid) return s.chatBot.isLoading;
    return s.chatBot.threadBuffers[tid]?.isLoading || false;
  });

  const regenerateDisabled = activeThreadIsStreaming;

  // Compute the previous user message synchronously so we can disable the
  // button when there's nothing to regenerate from.
  const priorUserText = useMemo<string | null>(() => {
    const idx = messages.findIndex((m) => m.id === messageId);
    if (idx <= 0) return null;
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === "user" && messages[i].text) {
        return messages[i].text;
      }
    }
    return null;
  }, [messages, messageId]);

  const handleRegenerate = useCallback(() => {
    if (regenerateDisabled) return;
    if (!priorUserText) return;
    dispatch(clearError());
    dispatch(
      sendMessage({
        message: priorUserText,
        machineId: machineContext?.machineId,
        threadId: activeThreadId || undefined,
        originThreadId: activeThreadId || undefined,
      })
    );
  }, [
    dispatch,
    regenerateDisabled,
    priorUserText,
    machineContext,
    activeThreadId,
  ]);

  // ─── Tooltip text ───────────────────────────────────────────────────
  const labels = {
    copy: translate("midas.v3.messageActions.copy", {
      defaultValue: "Copy",
    }) as string,
    copied: translate("midas.v3.messageActions.copied", {
      defaultValue: "Copied",
    }) as string,
    like: translate("midas.v3.messageActions.like", {
      defaultValue: "Like",
    }) as string,
    dislike: translate("midas.v3.messageActions.dislike", {
      defaultValue: "Dislike",
    }) as string,
    regenerate: translate("midas.v3.messageActions.regenerate", {
      defaultValue: "Regenerate",
    }) as string,
    helpsTune: translate("midas.v3.messageActions.helpsTune", {
      defaultValue: "helps tune Midas",
    }) as string,
  };

  // ─── Shared button styles ───────────────────────────────────────────
  // 26×26 ghost icon buttons. Rest: muted icon, transparent bg.
  // Hover: ink icon + card-2 bg. Active (like/dislike): accent-soft bg
  // with accent icon. Reflects the "ghost → accent" treatment from the
  // handoff.
  const baseBtnStyle: React.CSSProperties = {
    width: 26,
    height: 26,
    padding: 0,
    background: "transparent",
    border: "0",
    borderRadius: 6,
    cursor: "pointer",
    display: "inline-grid",
    placeItems: "center",
    color: "var(--muted)",
    transition: "background-color 120ms ease, color 120ms ease",
    appearance: "none",
    WebkitAppearance: "none",
  };

  const activeBtnStyle: React.CSSProperties = {
    background: "var(--accent-soft)",
    color: "var(--accent)",
  };

  const disabledBtnStyle: React.CSSProperties = {
    cursor: "not-allowed",
    opacity: 0.45,
  };

  const onHoverEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.currentTarget;
    // Don't override active state.
    if (target.dataset.active === "true") return;
    if (target.disabled) return;
    target.style.background = "var(--card-2)";
    target.style.color = "var(--ink)";
  };
  const onHoverLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.currentTarget;
    if (target.dataset.active === "true") return;
    target.style.background = "transparent";
    target.style.color = "var(--muted)";
  };

  // Determine whether the panel should render. We only render when panelAction
  // matches the corresponding active toggle — guards against the panel
  // lingering after the user toggles OFF then ON again very quickly.
  const showPanel =
    !!botMessageId &&
    ((panelAction === "like" && liked) ||
      (panelAction === "dislike" && disliked));

  return (
    <>
    <div
      // Indent so the row aligns with the response card text column, NOT the
      // avatar gutter — per spec §4.
      style={{
        marginLeft: 36,
        marginTop: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {/* Copy */}
        <button
          type="button"
          onClick={handleCopy}
          title={copied ? labels.copied : labels.copy}
          aria-label={copied ? labels.copied : labels.copy}
          style={baseBtnStyle}
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
        >
          {copied ? (
            <Check size={14} strokeWidth={2} color="var(--green)" />
          ) : (
            <Copy size={14} strokeWidth={1.75} />
          )}
        </button>

        {/* Like */}
        <button
          type="button"
          onClick={handleLike}
          title={labels.like}
          aria-label={labels.like}
          aria-pressed={liked}
          data-active={liked ? "true" : "false"}
          style={{
            ...baseBtnStyle,
            ...(liked ? activeBtnStyle : null),
          }}
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
        >
          <ThumbsUp
            size={14}
            strokeWidth={liked ? 2 : 1.75}
            {...(liked ? { fill: "currentColor" } : {})}
          />
        </button>

        {/* Dislike */}
        <button
          type="button"
          onClick={handleDislike}
          title={labels.dislike}
          aria-label={labels.dislike}
          aria-pressed={disliked}
          data-active={disliked ? "true" : "false"}
          style={{
            ...baseBtnStyle,
            ...(disliked ? activeBtnStyle : null),
          }}
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
        >
          <ThumbsDown
            size={14}
            strokeWidth={disliked ? 2 : 1.75}
            {...(disliked ? { fill: "currentColor" } : {})}
          />
        </button>

        {/* Regenerate — disabled when a stream is already in flight OR when
            we couldn't locate a prior user message to re-send. */}
        <button
          type="button"
          onClick={handleRegenerate}
          title={labels.regenerate}
          aria-label={labels.regenerate}
          disabled={regenerateDisabled || !priorUserText}
          style={{
            ...baseBtnStyle,
            ...((regenerateDisabled || !priorUserText) ? disabledBtnStyle : null),
          }}
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
        >
          <RotateCw size={14} strokeWidth={1.75} />
        </button>
      </div>

      <span
        className="mono"
        style={{
          fontSize: 10.5,
          color: "var(--faint)",
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        }}
      >
        {labels.helpsTune}
      </span>
    </div>

    {/* R5 sub-task 5b: optional comment panel. Only renders when the user has
        Like'd or Dislike'd AND the bot emitted a botMessageId (no panel for
        older messages we can't post against). The panel renders the second
        POST when the user submits. */}
    {showPanel && botMessageId && (
      <FeedbackPanelV3
        botMessageId={botMessageId}
        threadId={threadId}
        action={panelAction!}
        onClose={() => setPanelAction(null)}
        onMessageNotFound={handlePanelMessageNotFound}
      />
    )}
    </>
  );
};

export default MessageActionsV3;
